import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, timeAgo, formatApiError } from "../lib/api";
import Pager from "./Pager";
import { JoinOffers } from "./JoinOffers";
import { DemoBookings } from "./DemoBookings";
import { CoursesAdmin } from "./CoursesAdmin";

const fmtDT = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const OPS_SUBS = [["health", "System health"], ["quality", "Job quality"], ["audit", "Audit log"], ["security", "Security"], ["automations", "Automations"], ["experience", "Public experience"], ["joinoffers", "Join pages"], ["demo", "Demo bookings"], ["courses", "Courses"]];

export function Ops() {
  const [sub, setSub] = useState("health");
  return (
    <div data-testid="ops-tab">
      <div className="flex items-center gap-1 border border-zinc-200 rounded-md bg-white p-1 w-fit flex-wrap mb-4" data-testid="ops-subtabs">
        {OPS_SUBS.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`ops-subtab-${id}`}
            title={id === "health" ? "Live status of indexing, feeds, email, Stripe, Google sign-in and scheduled jobs"
              : id === "quality" ? "Duplicates, missing details and expiring listings, with bulk actions"
              : id === "audit" ? "Who changed what, when, every admin action is recorded"
              : id === "security" ? "Sign-in history and failed-login monitoring"
              : id === "joinoffers" ? "Dedicated sign-up pages for jobseekers and employers, with a welcome offer you set here"
              : id === "experience" ? "Choose the public desktop job-search layout: original list view or the new split view"
              : id === "demo" ? "Switch the 'Book a demo' button on or off and work through the requests"
              : id === "courses" ? "Real UK courses matched to jobs, sync, hide/show, switch the whole feature on or off"
              : "Switch platform automations on and off"}
            className={`h-8 px-3 text-xs font-semibold rounded transition-colors duration-200 ${sub === id ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>
            {label}
          </button>
        ))}
      </div>
      {sub === "health" && <SystemHealth />}
      {sub === "quality" && <JobQuality />}
      {sub === "audit" && <AuditLog />}
      {sub === "security" && <Security />}
      {sub === "automations" && <Automations />}
      {sub === "joinoffers" && <JoinOffers />}
      {sub === "demo" && <DemoBookings />}
      {sub === "courses" && <CoursesAdmin />}
      {sub === "experience" && <PublicExperience />}
    </div>
  );
}

// Owner/admin switch for the public desktop job-search layout. The original list
// view is always available and is the safe fallback; every change is audited.
export function PublicExperience() {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/settings")
    .then((r) => setMode(r.data.desktop_layout === "split" ? "split" : "list"))
    .catch((e) => toast.error(formatApiError(e, "Couldn't load settings")));
  useEffect(() => { load(); }, []);
  const choose = async (m) => {
    if (m === mode || busy) return;
    setBusy(true);
    try {
      await api.patch("/admin/settings", { desktop_layout: m });
      setMode(m);
      toast.success(m === "split" ? "Desktop split view is now live" : "Original list view is now live");
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't save that setting"));
    } finally {
      setBusy(false);
    }
  };
  if (!mode) return <div className="h-32 rounded-md bg-zinc-50 animate-pulse" data-testid="experience-loading" />;
  const cards = [
    ["list", "Original list view", "The job search exactly as it works today — a results list, and each job opens on its own page."],
    ["split", "Desktop split view", "Desktop only: results on the left, the selected job's full details on the right. Phones keep the existing mobile experience."],
  ];
  return (
    <div data-testid="public-experience">
      <p className="text-sm text-zinc-500 mb-3">Choose which public desktop job-search layout is live. The original list view is always the safe fallback if anything is unconfigured, and every change is recorded in the security audit log.</p>
      <p className="text-xs text-zinc-600 mb-3" data-testid="layout-current">Active now: <strong>{mode === "split" ? "Desktop split view" : "Original list view"}</strong></p>
      <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
        {cards.map(([id, label, desc]) => (
          <button key={id} type="button" onClick={() => choose(id)} disabled={busy} data-testid={`layout-option-${id}`}
            className={`text-left border rounded-md p-4 transition-colors duration-200 disabled:opacity-50 ${mode === id ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-500"}`}>
            <p className="text-sm font-bold">{label}</p>
            <p className={`text-xs mt-1 ${mode === id ? "text-zinc-300" : "text-zinc-500"}`}>{desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
const STATUS_CHIP = { ok: "bg-green-100 text-green-700", warn: "bg-amber-100 text-amber-700", down: "bg-red-100 text-red-700" };

export function SystemHealth() {
  const [data, setData] = useState(null);
  const [retrying, setRetrying] = useState("");
  const load = () => api.get("/admin/system/health").then((r) => setData(r.data)).catch(() => toast.error("Health check failed"));
  useEffect(() => { load(); }, []);
  const retry = async (key) => {
    setRetrying(key);
    try { await api.post(`/admin/partners/${key}/sync`); toast.success("Sync started, figures refresh in a minute"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setRetrying(""); }
    setTimeout(load, 4000);
  };
  if (!data) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="health-loading" />;
  return (
    <div data-testid="system-health">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-zinc-500">Everything critical in one place. Checked {timeAgo(data.checked_at)}.</p>
        <button onClick={load} data-testid="health-refresh"
          className="h-8 px-3 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 inline-flex items-center gap-1.5 transition-colors duration-200">
          <RefreshCw className="h-3.5 w-3.5" /> Re-check
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {data.sections.map((s) => (
          <div key={s.key} className="border border-zinc-200 rounded-md p-4 bg-white flex items-start justify-between gap-3" data-testid={`health-card-${s.key}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CHIP[s.status] || STATUS_CHIP.warn}`} data-testid={`health-status-${s.key}`}>
                  {s.status === "ok" ? "Healthy" : s.status === "warn" ? "Check" : "Down"}
                </span>
                <p className="text-sm font-semibold text-zinc-950 truncate">{s.label}</p>
              </div>
              <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{s.detail}</p>
              {s.at && <p className="text-[10px] text-zinc-400 mt-1">Last activity {timeAgo(s.at)}</p>}
            </div>
            {s.retry && s.status !== "ok" && (
              <button onClick={() => retry(s.retry)} disabled={!!retrying} data-testid={`health-retry-${s.key}`}
                className="h-8 px-3 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 shrink-0 transition-colors duration-200">
                {retrying === s.retry ? "Retrying…" : "Retry"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const Q_TABS = [["duplicates", "Possible duplicates"], ["missing_fields", "Missing details"], ["expiring_soon", "Expiring soon"], ["expired", "Expired"]];

export function JobQuality() {
  const [data, setData] = useState(null);
  const [sub, setSub] = useState("duplicates");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/jobs/quality").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load job quality"));
  useEffect(() => { load(); }, []);
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const bulk = async (action) => {
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Delete ${ids.length} job(s) for good? Their applications go too.`)) return;
    const note = action === "reject" ? (window.prompt("Reason shown to the employer (optional):") || "") : "";
    setBusy(action);
    try {
      const r = await api.post("/admin/jobs/bulk", { ids, action, note });
      toast.success(`${r.data.ok} done${r.data.failed.length ? ` · ${r.data.failed.length} skipped (${r.data.failed[0].reason})` : ""}`);
      setSel({}); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(""); }
  };
  if (!data) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="jq-loading" />;
  const rows = sub === "duplicates" ? data.duplicates.flat() : (data[sub] || []);
  const allChecked = rows.length > 0 && rows.every((r) => sel[r.id]);
  const rowExtra = (j) => sub === "missing_fields" ? `missing: ${(j.missing || []).join(", ")}`
    : sub === "duplicates" ? "" : `expires ${j.expires_at ? fmtDT(j.expires_at) : "—"}`;
  return (
    <div data-testid="job-quality">
      <p className="text-sm text-zinc-500 mb-3" title="Every check runs over pending and live listings">
        Automatic quality sweep, {data.counts.pending_review} jobs currently awaiting review. Select listings and apply one action to all of them.
      </p>
      <div className="flex items-center gap-1 border border-zinc-200 rounded-md bg-white p-1 w-fit flex-wrap" data-testid="jq-tabs">
        {Q_TABS.map(([id, label]) => (
          <button key={id} onClick={() => { setSub(id); setSel({}); }} data-testid={`jq-tab-${id}`}
            className={`h-8 px-3 text-xs font-semibold rounded transition-colors duration-200 ${sub === id ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>
            {label} <span className="ml-1 opacity-70">{data.counts[id] ?? 0}</span>
          </button>
        ))}
      </div>
      {ids.length > 0 && (
        <div className="mt-3 flex items-center gap-2 border border-blue-200 bg-blue-50/60 rounded-md px-3 py-2" data-testid="jq-bulk-bar">
          <span className="text-xs font-semibold text-blue-900">{ids.length} selected</span>
          {[["approve", "Approve & publish"], ["reject", "Reject"], ["pause", "Pause"], ["delete", "Delete"]].map(([a, lbl]) => (
            <button key={a} onClick={() => bulk(a)} disabled={!!busy} data-testid={`jq-action-${a}`}
              className={`h-8 px-3 text-xs font-semibold rounded-md border transition-colors duration-200 ${a === "delete" || a === "reject" ? "border-red-200 text-red-600 hover:bg-red-50" : "border-zinc-300 hover:border-zinc-500"}`}>
              {busy === a ? "Working…" : lbl}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 border border-zinc-200 rounded-md overflow-hidden bg-white" data-testid="jq-list">
        {rows.length === 0 ? (
          <p className="px-5 py-8 text-sm text-zinc-500 text-center" data-testid="jq-empty">Nothing flagged here, this category is clean.</p>
        ) : (
          <div className="max-h-[480px] overflow-y-auto">
            <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 sticky top-0 bg-white z-10">
              <input type="checkbox" checked={allChecked} onChange={(e) => setSel(Object.fromEntries(rows.map((r) => [r.id, e.target.checked])))}
                data-testid="jq-select-all" className="h-3.5 w-3.5 accent-zinc-900" />
              <span className="text-[11px] font-semibold text-zinc-500">Select all on this list</span>
            </div>
            {sub === "duplicates"
              ? data.duplicates.map((grp, gi) => (
                <div key={grp[0]?.id || `dup-group-${gi}`}>
                  <p className="px-4 py-1.5 bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-400 border-b border-zinc-100">Group {gi + 1}, same title & company</p>
                  {grp.map((j) => <QRow key={j.id} j={j} sel={sel} setSel={setSel} extra="" />)}
                </div>
              ))
              : rows.map((j) => <QRow key={j.id} j={j} sel={sel} setSel={setSel} extra={rowExtra(j)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function QRow({ j, sel, setSel, extra }) {
  return (
    <label className="px-4 py-2.5 flex items-center gap-3 border-b border-zinc-50 hover:bg-zinc-50/60 cursor-pointer transition-colors duration-150" data-testid={`jq-row-${j.id}`}>
      <input type="checkbox" checked={!!sel[j.id]} onChange={(e) => setSel({ ...sel, [j.id]: e.target.checked })}
        data-testid={`jq-check-${j.id}`} className="h-3.5 w-3.5 accent-zinc-900 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-zinc-900 truncate">{j.title} <span className="font-normal text-zinc-400">· {j.company_name}</span></p>
        <p className="text-[11px] text-zinc-500 truncate">{j.location || "no location"} · {j.tier} · {j.status}{extra ? ` · ${extra}` : ""}</p>
      </div>
    </label>
  );
}

export function AuditLog() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ action: "", actor: "", q: "" });
  const load = (page = 1, ff = f) => api.get("/admin/audit", { params: { page, limit: 25, ...ff } })
    .then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the audit log"));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="audit-loading" />;
  return (
    <div data-testid="audit-log">
      <p className="text-sm text-zinc-500 mb-3">Exactly who changed what, job approvals, pricing, refunds, verification decisions, campaigns, security actions.</p>
      <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="audit-filters">
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
          placeholder="Search anything" data-testid="audit-filter-q" className="flex-1 h-9 px-3 border border-zinc-300 rounded-md text-sm" />
        <select value={f.action} onChange={(e) => { const nf = { ...f, action: e.target.value }; setF(nf); load(1, nf); }}
          data-testid="audit-filter-action" className="sm:w-52 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
          <option value="">All actions</option>
          {(data.actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input value={f.actor} onChange={(e) => setF({ ...f, actor: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
          placeholder="Who (email)" data-testid="audit-filter-actor" className="sm:w-52 h-9 px-3 border border-zinc-300 rounded-md text-sm" />
        <button onClick={() => load(1)} data-testid="audit-filter-apply"
          className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Apply</button>
      </div>
      <div className="border border-zinc-200 rounded-md overflow-hidden bg-white" data-testid="audit-list">
        {data.items.length === 0 ? (
          <p className="px-5 py-8 text-sm text-zinc-500 text-center" data-testid="audit-empty">Nothing recorded yet, actions are logged from now on.</p>
        ) : (
          <div className="max-h-[480px] overflow-y-auto">
            {data.items.map((r, i) => (
              <div key={r.id} className={`px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap ${i > 0 ? "border-t border-zinc-100" : ""}`} data-testid={`audit-row-${i}`}>
                <div className="min-w-0">
                  <p className="text-xs text-zinc-800"><span className="font-semibold text-zinc-950">{r.actor || "system"}</span> · <span className="font-semibold text-blue-700">{r.action}</span></p>
                  {r.detail && <p className="text-[11px] text-zinc-500 truncate mt-0.5">{r.detail}</p>}
                </div>
                <span className="text-[11px] text-zinc-400 whitespace-nowrap tabular-nums shrink-0" data-testid={`audit-time-${i}`}>{fmtDT(r.at)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="px-4"><Pager page={data.page} total={data.total} onChange={(p) => load(p)} testid="audit-pager" /></div>
      </div>
    </div>
  );
}

export function Security() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ ok: "", q: "" });
  const load = (page = 1, ff = f) => api.get("/admin/security/logins", { params: { page, limit: 25, ...ff } })
    .then((r) => setData(r.data)).catch(() => toast.error("Couldn't load sign-in history"));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="security-loading" />;
  return (
    <div data-testid="security-tab">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="border border-zinc-200 rounded-md px-3.5 py-3 bg-white" data-testid="security-failed">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Failed sign-ins · 24h</p>
          <p className={`font-display text-xl font-black mt-0.5 tabular-nums ${data.failed_24h > 10 ? "text-red-600" : "text-zinc-950"}`}>{data.failed_24h}</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">accounts lock for 15 min after 5 failures</p>
        </div>
        <div className="border border-zinc-200 rounded-md px-3.5 py-3 bg-white" data-testid="security-total">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Sign-ins recorded</p>
          <p className="font-display text-xl font-black text-zinc-950 mt-0.5 tabular-nums">{data.total.toLocaleString("en-GB")}</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">user actions live in Users tab: deactivate, force reset, sign out everywhere</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="security-filters">
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") load(1); }}
          placeholder="Filter by email" data-testid="security-filter-q" className="flex-1 h-9 px-3 border border-zinc-300 rounded-md text-sm" />
        <select value={f.ok} onChange={(e) => { const nf = { ...f, ok: e.target.value }; setF(nf); load(1, nf); }}
          data-testid="security-filter-result" className="sm:w-44 h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white">
          <option value="">All results</option>
          <option value="1">Successful</option>
          <option value="0">Failed</option>
        </select>
        <button onClick={() => load(1)} data-testid="security-filter-apply"
          className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Apply</button>
      </div>
      <div className="border border-zinc-200 rounded-md overflow-hidden bg-white" data-testid="security-list">
        {data.items.length === 0 ? (
          <p className="px-5 py-8 text-sm text-zinc-500 text-center" data-testid="security-empty">No sign-ins recorded yet, they're logged from now on.</p>
        ) : (
          <div className="max-h-[480px] overflow-y-auto">
            {data.items.map((r, i) => (
              <div key={r.id} className={`px-4 py-2.5 flex items-center justify-between gap-3 ${i > 0 ? "border-t border-zinc-100" : ""}`} data-testid={`security-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${r.ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`} data-testid={`security-result-${i}`}>
                    {r.ok ? "Signed in" : "Failed"}
                  </span>
                  <p className="text-xs text-zinc-800 truncate">{r.email}{r.role ? <span className="text-zinc-400"> · {r.role}</span> : ""}</p>
                </div>
                <span className="text-[11px] text-zinc-400 whitespace-nowrap tabular-nums shrink-0">{fmtDT(r.at)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="px-4"><Pager page={data.page} total={data.total} onChange={(p) => load(p)} testid="security-pager" /></div>
      </div>
    </div>
  );
}

const RULES_META = [
  ["auto_approve_verified", "Auto-approve verified employers",
   "Jobs from verified companies skip review and go live the moment they're posted (after payment or a plan credit). Everything else still waits for your review.", "rule"],
  ["warn_before_expiry", "Warn employers before expiry",
   "Employers get an email heads-up before their listings expire, so renewals don't slip.", "built-in"],
  ["purge_expired_partner_jobs", "Remove expired partner jobs",
   "Expired Adzuna, Reed and GOV.UK vacancies are deactivated, then purged after 7 days.", "built-in"],
  ["alert_integration_down", "Integration health alerts",
   "The System health tab flags any feed, payment, email or indexing problem the moment checks run, with a retry button.", "built-in"],
  ["weekly_admin_summary", "Weekly Control Tower summary",
   "Every Monday morning: revenue, signups, jobs, applications and chat activity emailed to you.", "built-in"],
  ["welcome_and_inactivity_emails", "Welcome & inactivity emails",
   "New users are welcomed automatically; quiet users get friendly re-engagement emails with fresh roles near them.", "built-in"],
];

export function Automations() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState("");
  const load = () => api.get("/admin/automations").then((r) => setRules(r.data.rules)).catch(() => toast.error("Couldn't load automations"));
  useEffect(() => { load(); }, []);
  const toggle = async (key, value) => {
    setSaving(key);
    try {
      const r = await api.patch("/admin/automations", { [key]: value });
      setRules(r.data.rules);
      toast.success("Automation updated, applies immediately");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(""); }
  };
  if (!rules) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="automations-loading" />;
  return (
    <div data-testid="automations-tab">
      <p className="text-sm text-zinc-500 mb-3">Switch platform behaviour on and off. Built-in automations already run on the schedule; the rule changes how jobs are reviewed.</p>
      <div className="space-y-2">
        {RULES_META.map(([key, label, desc, kind]) => (
          <div key={key} className="border border-zinc-200 rounded-md px-4 py-3.5 bg-white flex items-center justify-between gap-4" data-testid={`automation-${key}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-zinc-950">{label}</p>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${kind === "rule" ? "bg-blue-50 text-blue-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {kind === "rule" ? "Rule" : "Built-in"}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{desc}</p>
            </div>
            <button onClick={() => toggle(key, !rules[key])} disabled={saving === key} data-testid={`automation-toggle-${key}`}
              title={rules[key] ? "Click to switch off" : "Click to switch on"}
              className={`relative h-6 w-11 rounded-full transition-colors duration-200 shrink-0 ${rules[key] ? "bg-blue-600" : "bg-zinc-300"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ${rules[key] ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
